import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, ArrowRight, Zap, Crown, Building, Building2 } from "lucide-react";
import { useSubscription, PLAN_LABELS, type PlanType } from "@/lib/subscriptionContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PlanComparisonSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const plans = [
  { key: "free" as PlanType, name: "Free", price: 0, annualPrice: 0, icon: Zap },
  { key: "pro" as PlanType, name: "Pro", price: 99, annualPrice: 79, icon: Crown },
  { key: "business" as PlanType, name: "Studio", price: 179, annualPrice: 143, icon: Building },
  { key: "enterprise" as PlanType, name: "Enterprise", price: null, annualPrice: null, icon: Building2 },
];

interface FeatureRow {
  label: string;
  values: Record<PlanType, string | boolean>;
  category: string;
}

// `starter` is kept in each row (mirrors the plan_type enum) but never rendered — the
// `plans` array no longer includes it, so values just satisfy the typed Record.
// Starter values mirror Free since the tier was collapsed into Free.
const features: FeatureRow[] = [
  // Planning
  { category: "Planning", label: "Roadmaps", values: { free: "3", starter: "3", pro: "10", business: "25", enterprise: "Unlimited" } },
  { category: "Planning", label: "PDF Export", values: { free: true, starter: true, pro: true, business: true, enterprise: true } },
  { category: "Planning", label: "White-label PDF", values: { free: false, starter: false, pro: false, business: true, enterprise: true } },
  { category: "Planning", label: "Financial Forecast", values: { free: true, starter: true, pro: true, business: true, enterprise: true } },
  { category: "Planning", label: "Permit Tracker", values: { free: "Full", starter: "Full", pro: "Full", business: "Full", enterprise: "Full" } },
  { category: "Planning", label: "Timeline", values: { free: "Full", starter: "Full", pro: "Full", business: "Full", enterprise: "Full" } },
  // Operations
  { category: "Operations", label: "Post-opening Mode", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "Operations", label: "Health Inspection Prep", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "Operations", label: "Lease Tracker", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "Operations", label: "Permit Renewals", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "Operations", label: "Survival Score", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "Operations", label: "Staff Rota & Calendar", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  // AI & Analytics
  { category: "AI & Analytics", label: "AI Questions", values: { free: "5", starter: "5", pro: "Unlimited", business: "Unlimited", enterprise: "Unlimited" } },
  { category: "AI & Analytics", label: "Location Map", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "AI & Analytics", label: "Analytics Dashboard", values: { free: false, starter: false, pro: false, business: true, enterprise: true } },
  { category: "AI & Analytics", label: "KPI Benchmarking", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  // Team & Support
  { category: "Team & Support", label: "Team Members", values: { free: "1", starter: "1", pro: "1", business: "5", enterprise: "Unlimited" } },
  { category: "Team & Support", label: "Priority Support", values: { free: false, starter: false, pro: true, business: true, enterprise: true } },
  { category: "Team & Support", label: "Dedicated Manager", values: { free: false, starter: false, pro: false, business: true, enterprise: true } },
  { category: "Team & Support", label: "SSO", values: { free: false, starter: false, pro: false, business: false, enterprise: true } },
  { category: "Team & Support", label: "API Access", values: { free: false, starter: false, pro: false, business: true, enterprise: true } },
  { category: "Team & Support", label: "Custom Integrations", values: { free: false, starter: false, pro: false, business: false, enterprise: true } },
];

function CellValue({ value, isCurrent }: { value: string | boolean; isCurrent: boolean }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className={`w-4 h-4 mx-auto ${isCurrent ? "text-primary" : "text-primary/70"}`} />
    ) : (
      <X className="w-4 h-4 mx-auto text-muted-foreground/30" />
    );
  }
  return <span className={`text-xs font-medium ${isCurrent ? "text-foreground" : "text-muted-foreground"}`}>{value}</span>;
}

export default function PlanComparisonSheet({ open, onOpenChange }: PlanComparisonSheetProps) {
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const currentPlan = subscription?.plan || "free";

  const handleSelect = async (key: PlanType) => {
    if (key === "enterprise") {
      onOpenChange(false);
      navigate("/enterprise");
      return;
    }
    if (key === currentPlan) return;
    // Downgrade directly, upgrade goes to checkout
    const currentIdx = plans.findIndex(p => p.key === currentPlan);
    const targetIdx = plans.findIndex(p => p.key === key);
    if (targetIdx < currentIdx) {
      toast.info("To change your plan, please contact support.");
      onOpenChange(false);
    } else {
      onOpenChange(false);
      navigate(`/checkout?plan=${key}&billing=monthly`);
    }
  };

  const categories = [...new Set(features.map(f => f.category))];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] p-0 rounded-t-2xl">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="font-heading text-2xl">Compare Plans</SheetTitle>
          <p className="text-sm text-muted-foreground">See what you get with each plan. Your current plan is highlighted.</p>
        </SheetHeader>

        <ScrollArea className="h-[calc(90vh-100px)]">
          <div className="px-4 py-4 overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse">
              {/* Plan headers */}
              <thead>
                <tr>
                  <th className="text-left py-3 px-3 w-[180px]" />
                  {plans.map(p => {
                    const isCurrent = p.key === currentPlan;
                    const Icon = p.icon;
                    return (
                      <th
                        key={p.key}
                        className={`text-center py-3 px-2 min-w-[120px] rounded-t-xl ${
                          isCurrent ? "bg-primary/5 border-x border-t border-primary/20" : ""
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isCurrent ? "bg-primary/10" : "bg-muted"
                          }`}>
                            <Icon className={`w-4 h-4 ${isCurrent ? "text-primary" : "text-muted-foreground"}`} />
                          </div>
                          <span className="font-heading font-medium text-sm text-foreground">{p.name}</span>
                          {isCurrent && (
                            <Badge variant="default" className="text-[10px] px-1.5 py-0">Current</Badge>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {p.price === null ? "Custom" : p.price === 0 ? "Free" : `$${p.price}/mo`}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {categories.map(cat => (
                  <>
                    <tr key={cat}>
                      <td colSpan={plans.length + 1} className="pt-4 pb-1.5 px-3">
                        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{cat}</span>
                      </td>
                    </tr>
                    {features.filter(f => f.category === cat).map(feat => (
                      <tr key={feat.label} className="border-b border-border/30 last:border-0">
                        <td className="py-2.5 px-3 text-sm text-foreground">{feat.label}</td>
                        {plans.map(p => {
                          const isCurrent = p.key === currentPlan;
                          return (
                            <td
                              key={p.key}
                              className={`py-2.5 px-2 text-center ${
                                isCurrent ? "bg-primary/5 border-x border-primary/20" : ""
                              }`}
                            >
                              <CellValue value={feat.values[p.key]} isCurrent={isCurrent} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                ))}

                {/* CTA row */}
                <tr>
                  <td className="py-4 px-3" />
                  {plans.map(p => {
                    const isCurrent = p.key === currentPlan;
                    const isDowngrade = plans.findIndex(x => x.key === currentPlan) > plans.findIndex(x => x.key === p.key);
                    return (
                      <td
                        key={p.key}
                        className={`py-4 px-2 text-center rounded-b-xl ${
                          isCurrent ? "bg-primary/5 border-x border-b border-primary/20" : ""
                        }`}
                      >
                        {isCurrent ? (
                          <span className="text-xs font-medium text-primary">Current plan</span>
                        ) : (
                          <Button
                            size="sm"
                            variant={isDowngrade ? "outline" : "default"}
                            className="gap-1.5 text-xs"
                            onClick={() => handleSelect(p.key)}
                          >
                            {p.key === "enterprise" ? "Contact us" : isDowngrade ? "Downgrade" : "Upgrade"}
                            <ArrowRight className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
